/**
 * Learning Center domain model — a faithful TypeScript port of the Flutter
 * `learning_center/domain/tour_definition.dart`. Same fields, same semantics,
 * so the tour data files transcribe 1:1 and the engine behaves identically.
 */

/** Where a tooltip should try to sit relative to its highlighted target. */
export type TourStepPlacement = 'top' | 'bottom' | 'left' | 'right' | 'auto';

/** How a step advances to the next one. */
export type TourStepInteraction =
  /** Tooltip shows a "Next" button; the underlying widget is blocked. */
  | 'info'
  /** Tooltip shows "Next"; the underlying widget stays interactive. */
  | 'allowInteract'
  /**
   * The engine never auto-advances. It leaves the real target tappable and
   * only moves on once the owning screen calls
   * `TourController.completeInteractiveStep` with this step's id.
   */
  | 'requireTap';

/**
 * One stop in a guided tour. [anchorId] is a key looked up in the DOM
 * (`[data-tour-anchor="<id>"]`) — null means a full-screen instructional
 * bubble with no spotlight cutout.
 */
export interface TourStep {
  id: string;
  screenId: string;
  anchorId?: string | null;
  title: string;
  description: string;
  placement?: TourStepPlacement;
  interaction?: TourStepInteraction;
  /**
   * For `requireTap` steps only: whether a tap landing inside the spotlighted
   * hole should, by itself, complete this step. Defaults to true. Set false
   * when the real completion signal is a later state change rather than the
   * tap itself.
   */
  autoCompleteOnTap?: boolean;
  /**
   * When true, the overlay draws neither the dimming spotlight nor the blocking
   * hit-bands — only the tooltip, so the whole screen stays interactive until
   * the step's real completion control advances the tour.
   */
  freeRoam?: boolean;
  /** Overrides which step the Back button returns to. */
  backTargetStepId?: string | null;
}

/** A single feature tour shown as a card on the Learning Center screen. */
export interface TourDefinition {
  id: string;
  name: string;
  description: string;
  /** Material Icons ligature name (rendered via the `material-icons` font). */
  icon: string;
  /** Estimated duration in seconds (used for the "n sec / n min" label). */
  estimatedSeconds: number;
  steps: TourStep[];
  /** Per-tour accent colour (hex). Defaults to the standard blue. */
  accentColor?: string;
}

/** A named collection of related tours rendered as one expandable card. */
export interface TourGroup {
  id: string;
  name: string;
  description: string;
  icon: string;
  accentColor?: string;
  tours: TourDefinition[];
}

export const DEFAULT_ACCENT = '#007AFF';

export function estimatedDurationLabel(seconds: number): string {
  if (seconds < 60) return `${seconds} sec`;
  return `${Math.round(seconds / 60)} min`;
}
